import { authedFetch, getJSON } from './http';
import type { Bundle, Prediction, Rules } from './wcTypes';
import { DEFAULT_RULES } from './wcTypes';

type Auth = { getToken: () => string | null; hydrate: () => Promise<void> };

/** GET /api/wc2026 (live) with the static bundle as an offline fallback. */
export async function fetchBundle(): Promise<Bundle> {
  try {
    return await getJSON<Bundle>('/api/wc2026');
  } catch {
    return getJSON<Bundle>('/data/wc2026.json');
  }
}

/** GET /api/rules — scoring weights, falls back to tournament defaults. */
export async function fetchRules(): Promise<Rules> {
  try {
    return await getJSON<Rules>('/api/rules');
  } catch {
    return DEFAULT_RULES;
  }
}

/** GET /api/predictions → Map<matchNumber, Prediction>. Requires auth. */
export async function fetchPredictions(auth: Auth): Promise<Map<number, Prediction>> {
  const body = await authedFetch<{ predictions: Record<string, Prediction> }>(
    auth.getToken,
    auth.hydrate,
    '/api/predictions',
  );
  const map = new Map<number, Prediction>();
  for (const p of Object.values(body.predictions ?? {})) map.set(p.matchNumber, p);
  return map;
}

/** PUT /api/predictions/{n} — throws ApiError (code: match_locked, boost_limit…). */
export async function savePrediction(
  auth: Auth,
  n: number,
  body: { scoreA: number; scoreB: number; boost: boolean },
): Promise<Prediction> {
  return authedFetch<Prediction>(auth.getToken, auth.hydrate, `/api/predictions/${n}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export type Distribution = { matchNumber: number; total: number; one: number; x: number; two: number };

/** GET /api/matches/{n}/distribution — crowd 1X2 split (public). */
export async function fetchDistribution(n: number): Promise<Distribution> {
  return getJSON<Distribution>(`/api/matches/${n}/distribution`);
}

/** GET /api/tournaments/{code}/distributions — one row per match with ≥1 pick. */
export async function fetchAllDistributions(code = 'wc2026'): Promise<Map<number, Distribution>> {
  const body = await getJSON<{ distributions: Distribution[] }>(
    `/api/tournaments/${code}/distributions`,
  );
  const map = new Map<number, Distribution>();
  for (const d of body.distributions ?? []) map.set(d.matchNumber, d);
  return map;
}
